import type { EventDetails } from "@/modules/eventDetails/client/api/types"
import type { FunctionComponent } from "react"

import { useParams } from "next/navigation"
import { useEffect } from "react"

import IconPlus from "@jobteaser/ui-kit/assets/icons/jds_plus.svg"
import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"

import { EXTERNAL, JOBTEASER, WITHOUT_REGISTRATION } from "@/modules/eventDetails/client/constants"
import { useEventDetailsData } from "@/modules/eventDetails/client/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

export type WaitingListButtonProps = {
  handleClick: (event: MouseEvent) => void
}

export const WaitingListButton: FunctionComponent<WaitingListButtonProps> = ({ handleClick }) => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const trackEvent = useTrackEvent()

  const { registrationDetails, id } = event as EventDetails

  const { type } = registrationDetails
  const isEventWithoutRegistration = type === WITHOUT_REGISTRATION
  const isExternalEvent = type === EXTERNAL
  const isJobteaserRegistration = type === JOBTEASER

  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const [subdomain] = hostname.split(".")

  useEffect(() => {
    trackEvent("event_ad_subscribe_display", {
      event_id: id,
      is_registering_on_waiting_list: true, // additionnal properties are allowed in the schema
      is_visible: true,
      service: "event_details",
      subdomain,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isExternalEvent || isEventWithoutRegistration) {
    return null
  }

  const onClick = (evt: MouseEvent): void => {
    trackEvent("event_ad_subscription_click", {
      context: "event_ad",
      event_ad_id: id,
      is_registering_on_waiting_list: true, // additionnal properties are allowed in the schema
      query_parameters: "",
      service: "event_details",
    })

    if (isJobteaserRegistration) {
      handleClick(evt)
    }
  }

  return (
    <Button
      onClick={onClick}
      variant={BUTTON_VARIANT.GHOST}
      icon={IconPlus}
      data-testid="event-details-registration-details-subscribe-button"
    >
      {t("event_details.subscribe_to_waiting_list")}
    </Button>
  )
}
